import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { ArrowRight, Loader2 } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.map(r => typeof r === "object" ? Object.entries(r).map(([k, x]) => `${k}: ${x ?? "—"}`).join(", ") : String(r)).join(" | ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};
const SUMMARY_LABELS = { title: "Title", name: "Name", po_number: "PO", supplier_name: "Supplier", project_name: "Project", customer_name: "Customer", form_name: "Form", invoice_number: "Invoice", change_order_number: "Change order", amount_delta: "Amount change", new_total: "New contract total", total: "Total", date: "Date", status: "Status", estimate_number: "Estimate", description: "Description", email_to: "Emails to" };

// Inline "what will change" panel for an approval request.
export const ApprovalPreview = ({ approvalId }) => {
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { axios.get(`${API}/approvals/${approvalId}/preview`).then(r => setP(r.data)).catch(e => setErr(e.response?.data?.detail || "Could not load preview")); }, [approvalId]);
  if (err) return <p className="text-xs text-red-600 mt-3">{err}</p>;
  if (!p) return <div className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading changes…</div>;
  const s = p.record_summary || {};
  const summaryRows = Object.entries(s).filter(([k]) => !["items", "milestone", "first_name", "last_name"].includes(k));
  const personName = [s.first_name, s.last_name].filter(Boolean).join(" ");

  return (
    <div className="mt-3 border-t pt-3 space-y-3 animate-fade-in" data-testid="approval-preview">
      {!p.record_found && <p className="text-xs text-amber-700">The original record could not be found (it may have been deleted already).</p>}

      {(personName || summaryRows.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs bg-slate-50 rounded-lg p-3">
          {personName && <div><span className="text-slate-500">Record</span><p className="font-medium text-slate-800">{personName}</p></div>}
          {summaryRows.map(([k, v]) => (
            <div key={k} className="min-w-0"><span className="text-slate-500">{SUMMARY_LABELS[k] || k}</span><p className="font-medium text-slate-800 truncate">{["amount_delta", "new_total", "total"].includes(k) ? money(v) : fmt(v)}</p></div>
          ))}
        </div>
      )}

      {p.action === "delete" && <p className="text-sm text-red-700 bg-red-50 rounded-lg p-3">Approving moves this <b>{p.collection?.replace(/_/g, " ").replace(/s$/, "")}</b> to Trash (recoverable for a while).</p>}

      {p.action === "send_po" && s.items && (
        <div className="text-sm">
          <p className="text-xs font-semibold text-slate-500 mb-1">Purchase order will be emailed with these items:</p>
          <div className="divide-y border rounded-lg">
            {s.items.map((i, idx) => <div key={idx} className="flex justify-between px-3 py-1.5"><span className="truncate">{i.name}</span><span className="text-slate-600 whitespace-nowrap ml-3">{i.quantity} {i.unit} × {money(i.unit_price)} = <b>{money(i.quantity * i.unit_price)}</b></span></div>)}
            <div className="flex justify-between px-3 py-1.5 font-semibold"><span>Total</span><span>{money(s.total)}</span></div>
          </div>
        </div>
      )}

      {p.action === "late_notice" && s.milestone && (
        <p className="text-sm bg-red-50 rounded-lg p-3">A late-payment notice will be sent for <b>{s.milestone.label}</b> — {money(s.milestone.amount)} due {s.milestone.due_date}.</p>
      )}

      {p.action === "change_order" && <p className="text-sm bg-indigo-50 rounded-lg p-3">Approving marks this change order as approved{s.amount_delta !== undefined ? ` (${money(s.amount_delta)} adjustment)` : ""} so it can be sent to the customer to sign.</p>}

      {p.changes?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-1">{p.changes.length} field{p.changes.length === 1 ? "" : "s"} will change:</p>
          <div className="space-y-1.5">
            {p.changes.map(c => (
              <div key={c.key} className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-2 text-sm" data-testid={`preview-change-${c.key}`}>
                <div className="min-w-0 bg-red-50 border border-red-100 rounded px-2 py-1"><p className="text-[10px] uppercase text-red-500">{c.label}</p><p className="line-through text-red-800 break-words">{fmt(c.old)}</p></div>
                <ArrowRight className="w-4 h-4 text-slate-400 mt-4" />
                <div className="min-w-0 bg-green-50 border border-green-100 rounded px-2 py-1"><p className="text-[10px] uppercase text-green-600">New</p><p className="font-medium text-green-900 break-words">{fmt(c.new)}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {p.action !== "delete" && p.action !== "send_po" && p.action !== "late_notice" && p.action !== "change_order" && p.changes?.length === 0 && (
        <p className="text-xs text-slate-500">No field differences from the current values{p.proposed_raw ? ` — proposed: ${fmt(p.proposed_raw)}` : ""}.</p>
      )}
    </div>
  );
};
